import {
  ExtractionFieldValue,
  ExtractionRecordValues,
  ExtractionResult,
  ExtractionReviewSchema,
  ExtractionReviewValues,
  TRI_BOOLEAN_OPTIONS,
} from "components/Document/ExtractionReview";
import {
  ApplyExtractedProfileInput,
  CharacterizationBasis,
  DensityUnit,
  ExtractedProfileReadingInput,
  ExtractedProfileValuesFieldsFragment,
  FlashPointQualifier,
  GetExtractedWasteProfileQuery,
  PackingGroup,
  PhysicalState,
  TemperatureUnit,
  WasteProfileInput,
  WasteStreamDotProfileInput,
} from "generated-graphql/graphql";

import {
  prettyPrintCharacterizationBasis,
  prettyPrintFlashPointQualifier,
  prettyPrintPackingGroup,
  prettyPrintPhysicalState,
} from "./profileDisplay";
import { WasteStreamProfileData } from "./types";

/** The repeated group key, which is also the extraction's array field path. */
const CONSTITUENTS = "constituents";

const physicalStateOptions = Object.values(PhysicalState).map((value) => ({
  value,
  label: prettyPrintPhysicalState(value) ?? value,
}));

const packingGroupOptions = Object.values(PackingGroup).map((value) => ({
  value,
  label: prettyPrintPackingGroup(value) ?? value,
}));

const basisOptions = Object.values(CharacterizationBasis).map((value) => ({
  value,
  label: prettyPrintCharacterizationBasis(value) ?? value,
}));

const flashPointQualifierOptions = Object.values(FlashPointQualifier).map((value) => ({
  value,
  label: prettyPrintFlashPointQualifier(value) ?? value,
}));

const temperatureUnitOptions = [
  { value: TemperatureUnit.Fahrenheit, label: "°F" },
  { value: TemperatureUnit.Celsius, label: "°C" },
];

const densityUnitOptions = [
  { value: DensityUnit.SpecificGravity, label: "Specific gravity (water = 1.0)" },
  { value: DensityUnit.GramsPerMl, label: "g/mL" },
  { value: DensityUnit.GramsPerCm3, label: "g/cm³" },
  { value: DensityUnit.KilogramsPerLiter, label: "kg/L" },
  { value: DensityUnit.PoundsPerGallon, label: "lb/gal" },
];

/**
 * The profile sheet's fields as the reviewer sees them.
 *
 * Every key is the extractor's own field path, so a confidence score and a
 * validation issue both address a control without anything in between having to
 * translate — the pipeline, the validator, and this grid all say `phLow` and
 * `constituents[0].casNumber`.
 *
 * Hazards are `triBoolean` rather than checkboxes because a sheet that never
 * mentions reactivity has said nothing about it, and an unchecked box would
 * commit that silence as a vendor asserting the waste is not reactive.
 */
export const WASTE_PROFILE_REVIEW_SCHEMA: ExtractionReviewSchema = {
  sections: [
    {
      key: "identity",
      title: "Profile",
      fields: [
        { key: "profileNumber", label: "Profile number", kind: "text" },
        { key: "tsdfName", label: "TSDF", kind: "text" },
        { key: "tsdfProfileId", label: "TSDF profile ID", kind: "text" },
      ],
    },
    {
      key: "physical",
      title: "Physical properties",
      fields: [
        {
          key: "physicalState",
          label: "Physical state",
          kind: "select",
          options: physicalStateOptions,
          width: "third",
        },
        { key: "phLow", label: "pH low", kind: "number", width: "third" },
        { key: "phHigh", label: "pH high", kind: "number", width: "third" },
        {
          key: "densityLow",
          label: "Density / specific gravity",
          kind: "number",
          helperText: "As printed",
          width: "third",
        },
        {
          key: "densityHigh",
          label: "Density high",
          kind: "number",
          helperText: "If a range",
          width: "third",
        },
        {
          key: "densityUnit",
          label: "Density unit",
          kind: "select",
          options: densityUnitOptions,
          width: "third",
        },
        {
          key: "flashPointValue",
          label: "Flash point",
          kind: "number",
          helperText: "Only when the sheet prints a measured value",
        },
        {
          key: "flashPointUnit",
          label: "Flash point unit",
          kind: "select",
          options: temperatureUnitOptions,
        },
        {
          key: "flashPointMinF",
          label: "Flash point range low (°F)",
          kind: "number",
          helperText: "Bounds of the range checked on the sheet; blank when open-ended",
          width: "third",
        },
        {
          key: "flashPointMaxF",
          label: "Flash point range high (°F)",
          kind: "number",
          width: "third",
        },
        {
          key: "flashPointQualifier",
          label: "Flash point qualifier",
          kind: "select",
          options: flashPointQualifierOptions,
          helperText: "When the sheet answers without a temperature",
          width: "third",
        },
        { key: "physicalDescription", label: "Appearance and odor", kind: "multiline" },
      ],
    },
    {
      key: "hazards",
      title: "Hazard characteristics",
      fields: [
        {
          key: "isIgnitable",
          label: "Ignitable",
          kind: "triBoolean",
          options: TRI_BOOLEAN_OPTIONS,
        },
        {
          key: "isCorrosive",
          label: "Corrosive",
          kind: "triBoolean",
          options: TRI_BOOLEAN_OPTIONS,
        },
        { key: "isReactive", label: "Reactive", kind: "triBoolean", options: TRI_BOOLEAN_OPTIONS },
        { key: "isToxic", label: "Toxic", kind: "triBoolean", options: TRI_BOOLEAN_OPTIONS },
      ],
    },
    {
      key: "codes",
      title: "Waste codes",
      fields: [
        {
          key: "federalWasteCodes",
          label: "EPA waste codes",
          kind: "text",
          helperText: "Comma separated, e.g. D001, F003",
        },
        {
          key: "stateWasteCodes",
          label: "State waste codes",
          kind: "text",
          helperText: "Comma separated",
        },
      ],
    },
    {
      key: "process",
      title: "Generating process",
      fields: [
        { key: "generatingProcess", label: "Process", kind: "multiline" },
        {
          key: "basis",
          label: "Characterization basis",
          kind: "select",
          options: basisOptions,
          helperText: "From the sheet's own “Knowledge is from” answer",
        },
      ],
    },
    {
      key: "dot",
      title: "DOT / transport",
      fields: [
        { key: "dotProperShippingName", label: "Proper shipping name", kind: "text" },
        { key: "dotIdNumber", label: "UN/NA number", kind: "text" },
        { key: "dotHazardClass", label: "Hazard class", kind: "text" },
        {
          key: "dotPackingGroup",
          label: "Packing group",
          kind: "select",
          options: packingGroupOptions,
        },
        {
          key: "dotHazardous",
          label: "DOT hazardous",
          kind: "triBoolean",
          options: TRI_BOOLEAN_OPTIONS,
        },
      ],
    },
  ],
  rowGroups: [
    {
      key: CONSTITUENTS,
      title: "Constituents",
      rowNoun: "constituent",
      // A chemical at a different percentage is the same constituent restated,
      // so the range is not part of what makes two rows the same row. Without
      // that, a sheet giving a new percentage for a chemical the profile already
      // lists reads as a constituent the profile does not have.
      identityColumns: ["name", "casNumber"],
      columns: [
        { key: "name", label: "Name", kind: "text" },
        { key: "casNumber", label: "CAS number", kind: "text" },
        { key: "minPercent", label: "Min", kind: "number", unit: "%" },
        { key: "maxPercent", label: "Max", kind: "number", unit: "%" },
      ],
    },
  ],
};

/** The keys of `T` that hold a single value rather than a nested object or list. */
type ScalarKeys<T> = {
  [K in keyof T]-?: NonNullable<T[K]> extends object ? never : K;
}[keyof T];

/**
 * The keys of `T` that hold a list of strings.
 *
 * A separate walk from `ScalarKeys` because that one excludes every object type, and
 * an array is one — so the code lists could not be typed against it and were left as
 * bare literals, which is the one thing the types below exist to prevent.
 */
type StringListKeys<T> = {
  [K in keyof T]-?: NonNullable<T[K]> extends readonly string[] ? K : never;
}[keyof T];

/**
 * A single-valued field the extraction reading actually has.
 *
 * Typed against the fragment rather than left as bare strings, so renaming a field
 * on the extractor fails the build in each of the lists below. Getting one wrong is
 * otherwise invisible: the grid renders a control whose value nothing supplies, and
 * the apply records the reviewer as having cleared a field they never saw.
 */
type ReadingField = Exclude<ScalarKeys<ExtractedProfileValuesFieldsFragment>, "__typename">;

/**
 * A reading that also has somewhere to fall back to.
 *
 * The intersection is the point: these lists drive a lookup against both the
 * reading and the record, so a field named here has to exist on both. That is the
 * invariant a bare string list cannot state — and getting it wrong is how a derived
 * column ends up in a list of readings.
 */
type ProfileBackedField = ReadingField & keyof NonNullable<WasteStreamProfileData["profile"]>;
type StreamBackedField = ReadingField & keyof WasteStreamProfileData;

/**
 * The waste-code lists, which are neither scalars nor a repeated row group.
 *
 * Kept out of the lists above because their type does not fit them: those drive a
 * direct assignment, and these need joining on the way in and splitting on the way
 * out.
 */
type CodeListField = StringListKeys<ExtractedProfileValuesFieldsFragment> &
  keyof NonNullable<WasteStreamProfileData["profile"]>;

const CODE_LIST_FIELDS: readonly CodeListField[] = [
  "federalWasteCodes",
  "stateWasteCodes",
] as const;

/** Fields whose stored value, when the sheet is silent, lives on the profile. */
const PROFILE_BACKED_FIELDS: readonly ProfileBackedField[] = [
  "profileNumber",
  "tsdfName",
  "tsdfProfileId",
  "physicalState",
  "phLow",
  "phHigh",
  "physicalDescription",
  "generatingProcess",
  "basis",
] as const;

/** Fields the DOT block hangs off the stream itself rather than the profile. */
const STREAM_BACKED_FIELDS: readonly StreamBackedField[] = [
  "dotProperShippingName",
  "dotIdNumber",
  "dotHazardClass",
  "dotPackingGroup",
] as const;

/**
 * Fields that seed from the reading or not at all.
 *
 * These columns default to false, so a stored false cannot be told apart from a
 * profile nobody has filled in, and pre-filling "No" is the assertion the
 * tri-state exists to avoid.
 */
const READING_ONLY_FIELDS: readonly ReadingField[] = [
  "isIgnitable",
  "isCorrosive",
  "isReactive",
  "isToxic",
  "dotHazardous",
  // Here for a different reason than the hazards: these are readings, and the
  // profile stores only what they resolve to — a converted flash point, a
  // collapsed specific gravity, a pair of bounds. There is no stored reading to
  // show through, and back-deriving one would invent a unit the sheet never
  // stated. A profile with a stored value and no reading shows it on the
  // read-only characterization card instead.
  "flashPointValue",
  "flashPointUnit",
  "flashPointMinF",
  "flashPointMaxF",
  "flashPointQualifier",
  "densityLow",
  "densityHigh",
  "densityUnit",
] as const;

/**
 * A code list as the reviewer edits it, normalized the way `toCodeList` writes it
 * back — uppercase and de-duplicated, order preserved.
 *
 * Both the seed and the record go through this, so the two sides are compared on
 * the same terms: "d001" stored against "D001" read off a sheet is one code typed
 * two ways, and reporting it as a value about to be replaced would name a
 * difference the apply itself normalizes away. Order is still significant here,
 * so the same codes listed in a different sequence do read as a difference — rare
 * enough to leave, and visible on the field when it happens.
 */
const joinCodes = (codes: readonly string[]): string | null => {
  const normalized = toCodeList(codes.join(","));
  return normalized.length > 0 ? normalized.join(", ") : null;
};

/** One reading's scalars, keyed by the field paths the grid and pipeline share. */
function toValues(
  reading: ExtractedProfileValuesFieldsFragment | null | undefined,
  fallback?: WasteStreamProfileData | null
): ExtractionResult["values"] {
  const profile = fallback?.profile ?? null;
  const values: Record<string, ExtractionFieldValue> = {};

  for (const field of PROFILE_BACKED_FIELDS) {
    values[field] = reading?.[field] ?? profile?.[field] ?? null;
  }
  for (const field of STREAM_BACKED_FIELDS) {
    values[field] = reading?.[field] ?? fallback?.[field] ?? null;
  }
  for (const field of READING_ONLY_FIELDS) {
    values[field] = reading?.[field] ?? null;
  }

  // Joined by hand rather than through the loops above, because a code list is the
  // one thing the grid cannot hold as-is: its value type is a single scalar, and a
  // list of codes is easier to check and edit as the text a sheet prints than as a
  // control the shared grid would have to grow. Split back apart on apply.
  for (const field of CODE_LIST_FIELDS) {
    // Falls back on an EMPTY reading, not just a missing one. These lists are
    // non-null on the extraction, so a sheet stating no codes arrives as `[]` — and
    // `??` reads that as an answer, blanks the control, and the apply then writes the
    // blank over codes the profile already holds. A sheet that lists none is silent
    // about them, not a statement that there are none.
    const extracted = reading?.[field] ?? [];
    values[field] = joinCodes(extracted.length > 0 ? extracted : (profile?.[field] ?? []));
  }

  return values;
}

/**
 * The sheet's own composition, and only that.
 *
 * The stored rows do not fall back into here any more: they reach the grid as the
 * record, which appends the ones the sheet does not list and flags them as rows
 * to keep or drop deliberately. Seeding them here instead made them
 * indistinguishable from a reading — and, when the sheet listed any composition
 * at all, dropped them from the grid entirely while the apply still replaced the
 * whole list.
 */
function toRows(
  reading: ExtractedProfileValuesFieldsFragment | null | undefined
): NonNullable<ExtractionResult["rows"]> {
  return {
    [CONSTITUENTS]: (reading?.constituents ?? []).map((constituent) => ({
      name: constituent.name ?? null,
      casNumber: constituent.casNumber ?? null,
      minPercent: constituent.minPercent ?? null,
      maxPercent: constituent.maxPercent ?? null,
    })),
  };
}

/**
 * The reading in the shape the shared grid consumes.
 *
 * `values` is what the reviewer sees: the model's reading with their own saved
 * corrections over it, and the stream's stored characterization showing through
 * wherever the sheet said nothing — the grid is applied field for field, so a
 * blank there is written as null, and seeding from the reading alone erased work
 * someone had typed by hand.
 *
 * `extractedValues` is what the model itself read, which the grid shows as
 * "Suggested: x" beside anything that no longer matches and offers back on
 * click. Without it a reviewer's own earlier correction is presented to them as
 * the extraction, and the model's reading is gone from the screen.
 */
export function toExtractionResult(
  reading: NonNullable<GetExtractedWasteProfileQuery["extractedWasteProfile"]>,
  current?: WasteStreamProfileData | null
): ExtractionResult {
  return {
    values: toValues(reading.values, current),
    rows: toRows(reading.values),
    extractedValues: toValues(reading.asExtracted),
    extractedRows: toRows(reading.asExtracted),
    // The API already keys confidence by field path, which is the form the grid
    // addresses a cell by, so this passes through unmapped.
    confidence: reading.confidence
      .filter((entry) => entry.confidence != null)
      .map((entry) => ({ field: entry.fieldPath, score: entry.confidence ?? 0 })),
  };
}

/**
 * What the stream already holds, in the grid's own terms, so the review can say
 * where it disagrees rather than quietly winning.
 *
 * Three deliberate omissions:
 *
 * The hazard flags and `dotHazardous`, because their columns default to `false`
 * and a stored `false` cannot be told from a profile nobody has filled in —
 * flagging it would put "the record says No" in front of the reviewer on every
 * profile that has never been characterized.
 *
 * `densityHigh`, because the profile stores only the specific gravity a range
 * collapses to; there is no stored high bound to disagree with.
 *
 * The measured flash point and the density, because neither can be handed over in
 * terms the form would read back correctly. Both stored columns are normalized —
 * `flashPointF` is Fahrenheit, `specificGravity` is dimensionless — while the
 * fields they would land in are readings whose unit is a *separate live field*
 * seeded from the sheet. A sheet printing "23 °C" over a profile storing 120 °F
 * would offer the reviewer "use 120" and then write `toFahrenheit(120, CELSIUS)`
 * — 248 °F, a number neither source ever held. Density is worse: taking the
 * stored specific gravity while the sheet's high bound stays put stores the
 * midpoint of the two.
 *
 * So they are left out for now, which means the clear this surface performs on
 * those two fields happens with the field visibly empty and no warning beside it.
 * The flash point can be closed by reporting its unit alongside the value: a
 * silent sheet leaves both blank, so taking the stored number writes it in the
 * unit the apply assumes, and a sheet stating Celsius flags both fields so a
 * half-take announces itself. Density cannot — a blank record value never flags,
 * so nothing would mark the leftover high bound — and needs the grid to let one
 * record value set its companion fields.
 *
 * This is not the seeding the grid does — a stored value is deliberately not
 * pre-filled into a reading field, since the sheet did not say it. Naming it as
 * the record is the opposite claim, and the one that keeps a silent sheet from
 * clearing it unseen.
 */
export function toRecordValues(
  current: WasteStreamProfileData | null | undefined
): ExtractionRecordValues {
  // No stream is nothing to compare against, which is not the same claim as a
  // stream whose profile is empty — that one has values, they are all null.
  if (!current) return {};
  const profile = current.profile ?? null;

  const values: Record<string, ExtractionFieldValue> = {};
  for (const field of PROFILE_BACKED_FIELDS) {
    values[field] = profile?.[field] ?? null;
  }
  for (const field of STREAM_BACKED_FIELDS) {
    values[field] = current[field] ?? null;
  }
  for (const field of CODE_LIST_FIELDS) {
    values[field] = joinCodes(profile?.[field] ?? []);
  }
  // The bounds and the qualifier need no unit to be read back: the columns are
  // already Fahrenheit and the field names say so, and the qualifier is the same
  // enum on both sides. These are safe to state and safe to take.
  values.flashPointMinF = profile?.flashPointMinF ?? null;
  values.flashPointMaxF = profile?.flashPointMaxF ?? null;
  values.flashPointQualifier = profile?.flashPointQualifier ?? null;

  return {
    values,
    rows: {
      [CONSTITUENTS]: (profile?.constituents ?? []).map((constituent) => ({
        name: constituent.name,
        casNumber: constituent.casNumber ?? null,
        minPercent: constituent.minPercent ?? null,
        maxPercent: constituent.maxPercent ?? null,
      })),
    },
  };
}

const PHYSICAL_STATES: readonly string[] = Object.values(PhysicalState);
const FLASH_POINT_QUALIFIERS: readonly string[] = Object.values(FlashPointQualifier);
const PACKING_GROUPS: readonly string[] = Object.values(PackingGroup);
const BASES: readonly string[] = Object.values(CharacterizationBasis);

/**
 * Narrow a reviewed value back to its enum.
 *
 * The grid holds every value as a string, boolean, or number, because it is
 * schema-agnostic by design. An unrecognized string resolves to null rather than
 * being forced through — the mutation would reject it anyway, and null reads as
 * "not answered", which is what an unusable value amounts to.
 */
const isPhysicalState = (value: string): value is PhysicalState => PHYSICAL_STATES.includes(value);
const isPackingGroup = (value: string): value is PackingGroup => PACKING_GROUPS.includes(value);
const isFlashPointQualifier = (value: string): value is FlashPointQualifier =>
  FLASH_POINT_QUALIFIERS.includes(value);

const toPhysicalState = (value: ExtractionFieldValue | undefined): PhysicalState | null =>
  typeof value === "string" && isPhysicalState(value) ? value : null;

const toPackingGroup = (value: ExtractionFieldValue | undefined): PackingGroup | null =>
  typeof value === "string" && isPackingGroup(value) ? value : null;

const toFlashPointQualifier = (
  value: ExtractionFieldValue | undefined
): FlashPointQualifier | null =>
  typeof value === "string" && isFlashPointQualifier(value) ? value : null;

const TEMPERATURE_UNITS: readonly string[] = Object.values(TemperatureUnit);
const DENSITY_UNITS: readonly string[] = Object.values(DensityUnit);

const toTemperatureUnit = (value: ExtractionFieldValue | undefined): TemperatureUnit | null =>
  typeof value === "string" && TEMPERATURE_UNITS.includes(value)
    ? (value as TemperatureUnit)
    : null;

const toDensityUnit = (value: ExtractionFieldValue | undefined): DensityUnit | null =>
  typeof value === "string" && DENSITY_UNITS.includes(value) ? (value as DensityUnit) : null;

const isBasis = (value: string): value is CharacterizationBasis => BASES.includes(value);

const toBasis = (value: ExtractionFieldValue | undefined): CharacterizationBasis | null =>
  typeof value === "string" && isBasis(value) ? value : null;

/**
 * Split an edited code list back into the array the profile stores.
 *
 * Uppercased and de-duplicated, because "d001, D001" is one code typed twice rather
 * than two codes, and the codes are compared against a regulation that writes them
 * in capitals. Order is preserved so a reviewer sees back what they typed.
 */
const toCodeList = (value: ExtractionFieldValue | undefined): string[] => {
  if (typeof value !== "string") return [];
  const seen = new Set<string>();
  for (const code of value.split(",")) {
    const trimmed = code.trim().toUpperCase();
    if (trimmed !== "") seen.add(trimmed);
  }
  return [...seen];
};

const toText = (value: ExtractionFieldValue | undefined): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
};

/**
 * Numbers pass through, and so does a string the grid could not read as one.
 *
 * Nulling an unreadable value would hide it: the reviewer typed something, and
 * the mutation takes these as `FlexibleFloat`, which keeps a non-numeric string
 * intact so the engine can refuse it by name. Dropping it here would apply the
 * profile with the field silently empty instead.
 */
const toNumber = (value: ExtractionFieldValue | undefined): number | string | null => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() !== "") return value;
  return null;
};

const toTriBoolean = (value: ExtractionFieldValue | undefined): boolean | null =>
  typeof value === "boolean" ? value : null;

/** The profile half of an apply, built from what the reviewer confirmed. */
export function toProfileInput(
  wasteStreamId: string,
  reviewed: ExtractionReviewValues
): WasteProfileInput {
  const { values, rows } = reviewed;
  return {
    wasteStreamId,
    physicalState: toPhysicalState(values.physicalState),
    phLow: toNumber(values.phLow),
    phHigh: toNumber(values.phHigh),
    // Narrowed to the enum, not passed through `toNumber`: an enum input has no
    // FlexibleFloat escape hatch, so an unreadable value must resolve to null
    // rather than travel to a mutation that would reject it.
    basis: toBasis(values.basis),
    federalWasteCodes: toCodeList(values.federalWasteCodes),
    stateWasteCodes: toCodeList(values.stateWasteCodes),
    physicalDescription: toText(values.physicalDescription),
    isIgnitable: toTriBoolean(values.isIgnitable),
    isCorrosive: toTriBoolean(values.isCorrosive),
    isReactive: toTriBoolean(values.isReactive),
    isToxic: toTriBoolean(values.isToxic),
    generatingProcess: toText(values.generatingProcess),
    profileNumber: toText(values.profileNumber),
    tsdfName: toText(values.tsdfName),
    tsdfProfileId: toText(values.tsdfProfileId),
    // Sent in full, including a row left wholly blank, and each carrying the
    // extraction row it came from. The extraction's field paths address a
    // constituent by position (`constituents[0].casNumber`), which the reviewed
    // list's own position stops matching the moment a row is deleted — so the
    // anchor travels with the row rather than being inferred from where it sits.
    // The empty row is dropped at the write, where nothing counts on the index.
    constituents: (rows[CONSTITUENTS] ?? []).map((row) => ({
      extractedIndex: row.extractedIndex,
      name: toText(row.values.name) ?? "",
      casNumber: toText(row.values.casNumber),
      minPercent: toNumber(row.values.minPercent),
      maxPercent: toNumber(row.values.maxPercent),
    })),
  };
}

/** The DOT half, which Project 2 hangs off the stream rather than the profile. */
export function toDotInput(
  wasteStreamId: string,
  reviewed: ExtractionReviewValues
): WasteStreamDotProfileInput {
  const { values } = reviewed;
  return {
    wasteStreamId,
    dotProperShippingName: toText(values.dotProperShippingName),
    dotIdNumber: toText(values.dotIdNumber),
    dotHazardClass: toText(values.dotHazardClass),
    dotPackingGroup: toPackingGroup(values.dotPackingGroup),
    dotHazardous: toTriBoolean(values.dotHazardous),
  };
}

export function toApplyInput(
  wasteStreamId: string,
  documentId: string,
  reviewed: ExtractionReviewValues
): ApplyExtractedProfileInput {
  return {
    documentId,
    profile: toProfileInput(wasteStreamId, reviewed),
    dot: toDotInput(wasteStreamId, reviewed),
    asRead: toReadingInput(reviewed),
  };
}

/**
 * The readings the reviewer confirmed, sent as printed rather than as the values
 * they resolve to.
 *
 * The server converts and collapses them, so one implementation decides what a
 * band, a temperature scale and a density unit are worth — and a correction is
 * recorded against the field the extractor actually produced rather than against
 * a derived column that has no reading to diff against.
 */
export function toReadingInput(reviewed: ExtractionReviewValues): ExtractedProfileReadingInput {
  const { values } = reviewed;
  return {
    flashPointValue: toNumber(values.flashPointValue),
    flashPointUnit: toTemperatureUnit(values.flashPointUnit),
    flashPointMinF: toNumber(values.flashPointMinF),
    flashPointMaxF: toNumber(values.flashPointMaxF),
    flashPointQualifier: toFlashPointQualifier(values.flashPointQualifier),
    densityLow: toNumber(values.densityLow),
    densityHigh: toNumber(values.densityHigh),
    densityUnit: toDensityUnit(values.densityUnit),
  };
}
